// Thin API client. Reads the CSRF token from cookie on every mutating call so
// we don't have to thread it through every component.

const API_BASE = import.meta.env.VITE_API_BASE ?? "https://ai-logo-api.spacesswift.workers.dev";

async function call<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const method = (init?.method ?? (init?.json ? "POST" : "GET")).toUpperCase();
  const headers = new Headers(init?.headers);
  if (init?.json !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  // The X-Requested-With header forces a CORS preflight on every mutating
  // request (including multipart uploads), so a malicious origin can't even
  // get a request through without an allow-listed preflight. The server's
  // Origin allow-list is the actual CSRF defense; this header is the lever.
  if (method !== "GET" && method !== "HEAD") {
    headers.set("X-Requested-With", "motion-web");
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers,
    body: init?.json !== undefined ? JSON.stringify(init.json) : init?.body,
  });
  if (!res.ok) {
    const text = await res.text();
    let parsed: any; try { parsed = JSON.parse(text); } catch { parsed = { error: text }; }
    const err = new Error(parsed?.error ?? `http_${res.status}`);
    (err as any).status = res.status;
    (err as any).body = parsed;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("Content-Type") ?? "";
  if (ct.includes("application/json")) return res.json() as Promise<T>;
  return (await res.text()) as unknown as T;
}

export const api = {
  signup:   (email: string, password: string) => call<{ ok: true; user: { id: string; email: string } }>("/api/v1/auth/signup", { json: { email, password } }),
  login:    (email: string, password: string) => call<{ ok: true; user: { id: string; email: string } }>("/api/v1/auth/login", { json: { email, password } }),
  logout:   () => call<{ ok: true }>("/api/v1/auth/logout", { json: {} }),
  me:       () => call<{ user: { id: string; email: string; createdAt: number } }>("/api/v1/auth/me"),

  templates:   () => call<{ templates: Array<{ id: string; title: string; blurb: string; bestFor: string[] }> }>("/api/v1/templates"),

  uploadLogo:  (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return call<{ asset: { id: string; key: string; mime: string; kind: string; bytes: number; sha256: string } }>("/api/v1/uploads/logo", { method: "POST", body: fd });
  },
  assetUrl:    (id: string) => `${API_BASE}/api/v1/uploads/asset/${id}`,

  createAnimation: (sourceAssetId: string, prompt: string, opts?: { width?: number; height?: number; fps?: number; model?: string }) =>
    call<{ animation: { id: string; status: string } }>("/api/v1/animations", { json: { sourceAssetId, prompt, ...opts } }),
  listAnimations: () => call<{ animations: AnimationDto[] }>("/api/v1/animations"),
  getAnimation:   (id: string) => call<{ animation: AnimationDto; asset: { id: string; r2Key: string; mime: string } | null }>(`/api/v1/animations/${id}`),
  patchIR:        (id: string, ir: Record<string, unknown>) => call<{ animation: AnimationDto }>(`/api/v1/animations/${id}/ir`, { method: "PATCH", json: ir }),
  regenerate:     (id: string, opts?: { prompt?: string; model?: string }) => call<{ animation: { id: string; status: string } }>(`/api/v1/animations/${id}/regenerate`, { json: opts ?? {} }),
  deleteAnim:     (id: string) => call<{ ok: true }>(`/api/v1/animations/${id}`, { method: "DELETE" }),

  uploadRender:   (animationId: string, blob: Blob, kind: "render_mp4" | "render_webm" | "render_gif", filename: string) => {
    const fd = new FormData();
    fd.append("file", new File([blob], filename, { type: blob.type }));
    fd.append("kind", kind);
    return call<{ asset: { id: string; key: string; kind: string; bytes: number } }>(`/api/v1/renders/${animationId}/upload`, { method: "POST", body: fd });
  },
  downloadUrl:    (animationId: string) => `${API_BASE}/api/v1/renders/${animationId}/download`,
};

export interface AnimationDto {
  id: string;
  prompt: string;
  templateId: string | null;
  status: "queued" | "analyzing" | "planning" | "rendering" | "encoding" | "ready" | "failed" | "cancelled";
  sourceAssetId: string;
  width: number; height: number; fps: number;
  durationMs: number | null;
  errorCode: string | null; errorMsg: string | null;
  ir: any | null;
  timeline: any | null;
  createdAt: number; updatedAt: number;
}
